import { metafieldTypesFor, type FieldStep, type MetafieldType } from '@forecastify/shared';
import { Badge, Icon, Select, TextInput } from '@forecastify/ui';
import type { Translate } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C34 · the wizard's seventh step. P2-20.                                   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EVERY ROW DEFAULTS TO *DON'T SYNC*.** A step arriving with twenty fields pre-ticked writes
 * a client's Jira configuration into this workspace's task schema, and whoever presses Next has
 * agreed to something they never read.
 *
 * ⚠️ **The picker offers exactly what the writer can fill**, from `metafieldTypesFor` — the same
 * function the server validates with. A target the writer cannot fill is a silent no-op: nothing
 * errors, nothing appears, and nobody can tell why.
 */

/** What one row currently says. `null` target is *don't sync*. */
export interface FieldChoice {
  readonly target:
    | null
    | { readonly kind: 'existing'; readonly metafieldId: string }
    | {
        readonly kind: 'create';
        readonly name: string;
        readonly type: MetafieldType;
        /**
         * ⚠️⚠️ **REQUIRED FOR `select` AND `multi`, AND ITS ABSENCE USED TO BE A 500.**
         * `metafields_options_match_type` (0016) refuses one of those with an empty list, and this
         * step sent none — there was no input for them — so mapping any Jira single-select to a new
         * metafield ended in a bare `check_violation` from Postgres with nothing on the screen.
         */
        readonly options: readonly string[];
      };
}

/** ⚠️ The two that need a list. Everything else must be sent an EMPTY one, not merely a missing one. */
const NEEDS_OPTIONS = (type: MetafieldType): boolean => type === 'select' || type === 'multi';

export function FieldsStep({
  t,
  step,
  needsSave,
  choices,
  onChange,
}: {
  readonly t: Translate;
  readonly step: FieldStep | null;
  /**
   * ⚠️⚠️ **TRUE ONLY WHEN THERE IS NEITHER A SAVED INSTANCE NOR A CONNECTION TEST - item 67.**
   * It used to be passed as *"the field step is null"*, which is also true for the whole of the
   * round trip that fetches it — so a saved instance was told to save itself, and the loading
   * branch below could never be reached. A field map points at an instance id; a probe stands in
   * for one because the wizard writes the map the moment the instance is created.
   */
  readonly needsSave: boolean;
  readonly choices: Record<string, FieldChoice>;
  readonly onChange: (remoteFieldId: string, choice: FieldChoice) => void;
}) {
  if (needsSave) {
    return (
      <p className="text-muted-foreground max-w-[70ch] text-[12px] leading-relaxed">
        {t('integrations.fields.saveFirst')}
      </p>
    );
  }
  /* ⚠️ Reachable at last, and it is the ordinary case on every saved instance. */
  if (step === null) {
    return <p className="text-muted-foreground text-[12px]">{t('integrations.fields.loading')}</p>;
  }

  /**
   * ⚠️⚠️ **THE MAPPABLE ONES FIRST, AND THE REST BELOW A LINE.** A stock Jira site answers with 85
   * fields, 41 of them custom and most of those Atlassian's own apps — a customer who opens this to
   * forty rows of things we cannot do concludes the feature is broken (§10.4).
   */
  const mappable = step.discovered.filter(
    (field) => !field.claimed && metafieldTypesFor(field.remoteType).length > 0,
  );
  const rest = step.discovered.filter(
    (field) => field.claimed || metafieldTypesFor(field.remoteType).length === 0,
  );

  return (
    <div className="flex flex-col gap-3" data-testid="integration-fields">
      <p className="text-muted-foreground text-[12px] leading-relaxed">
        {t('integrations.fields.lede')}
      </p>
      {step.discoveredComplete ? null : (
        /* ⚠️ The per-project walk stopped at its deadline (hard-won C36-5): a short list on a large
           site is a partial one, not a broken one, and the step says which. */
        <p className="text-muted-foreground text-[12px] leading-relaxed">
          {t('integrations.fields.partial')}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border">
        {mappable.map((field) => {
          const allowed = metafieldTypesFor(field.remoteType);
          const choice = choices[field.remoteFieldId]?.target ?? null;
          const value =
            choice === null ? '' : choice.kind === 'create' ? 'create' : choice.metafieldId;

          return (
            /**
             * ⚠⚠ **THE NAME AND THE CONTROLS ARE TWO GROUPS, NOT SIX SIBLINGS IN ONE WRAP.** As one
             * flex row, the fixed-width selects wrapped individually and the `flex-1` name beside
             * them collapsed to a sliver — a row whose left-hand column was a few pixels of ellipsis
             * with the controls stacked underneath. Nesting them means the controls wrap among
             * themselves and the name keeps a floor of its own.
             */
            <div
              key={field.remoteFieldId}
              className="flex flex-col gap-2 border-b px-3 py-2 last:border-0 sm:flex-row sm:items-start sm:gap-3"
            >
              <div className="min-w-[160px] flex-1 basis-[160px] pt-1.5">
                <span className="block truncate text-[12px]">{field.name}</span>
                {/* ⚠️ **The ID, always** — §10.3 found two fields called Story Points on a stock
                    site, different ids, one of them empty. Resolving by name maps the wrong one. */}
                <span className="text-muted-foreground block truncate text-[10.5px]">
                  {field.remoteFieldId} · {field.remoteType}
                </span>
              </div>

              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <Select
                  value={value}
                  aria-label={field.name}
                  options={[
                    { value: '', label: t('integrations.fields.none') },
                    ...step.metafields
                      .filter((meta) => allowed.includes(meta.type))
                      .map((meta) => ({
                        value: meta.id,
                        /* ⚠️ A required metafield cannot receive: one blank in Jira would block the
                         whole task. Shown and disabled, with the reason. */
                        label: meta.required
                          ? `${meta.name} · ${t('integrations.fields.requiredNo')}`
                          : meta.name,
                        disabled: meta.required,
                      })),
                    ...(step.canCreate
                      ? [{ value: 'create', label: t('integrations.fields.create') }]
                      : []),
                  ]}
                  onChange={(event) => {
                    const next = event.target.value;
                    if (next === '') onChange(field.remoteFieldId, { target: null });
                    else if (next === 'create') {
                      /* ⚠️ The first of the allowed set — the table's order is the preference. */
                      const type = allowed[0] ?? 'text';
                      onChange(field.remoteFieldId, {
                        target: {
                          kind: 'create',
                          name: field.name,
                          type,
                          /**
                           * ⚠⚠ **SEEDED FROM WHAT THE TRACKER ALREADY KNOWS.** YouTrack sends a
                           * bundle's values and Jira's `createmeta` sends `allowedValues`, so a
                           * select usually arrives with its list — and retyping a customer's twenty
                           * option labels by hand is how they end up not matching.
                           */
                          options: NEEDS_OPTIONS(type) ? field.options : [],
                        },
                      });
                    } else {
                      onChange(field.remoteFieldId, {
                        target: { kind: 'existing', metafieldId: next },
                      });
                    }
                  }}
                  className="w-[210px] shrink-0"
                />

                {choice?.kind === 'create' ? (
                  <>
                    <TextInput
                      value={choice.name}
                      aria-label={t('integrations.fields.newName')}
                      onChange={(event) => {
                        onChange(field.remoteFieldId, {
                          target: { ...choice, name: event.target.value },
                        });
                      }}
                      className="w-[170px] shrink-0 text-[12px]"
                    />
                    <Select
                      value={choice.type}
                      aria-label={t('integrations.fields.newType')}
                      options={allowed.map((type) => ({ value: type, label: type }))}
                      onChange={(event) => {
                        const type = event.target.value as MetafieldType;
                        onChange(field.remoteFieldId, {
                          target: {
                            ...choice,
                            type,
                            /* ⚠️ Cleared when the type stops taking a list, because the constraint
                               refuses options on anything but a select — in BOTH directions. */
                            options: NEEDS_OPTIONS(type)
                              ? choice.options.length > 0
                                ? choice.options
                                : field.options
                              : [],
                          },
                        });
                      }}
                      className="w-[110px] shrink-0"
                    />
                  </>
                ) : null}
              </div>

              {/**
               * ⚠⚠ **THE OPTIONS, ON THEIR OWN LINE, AND ONLY WHERE THEY ARE REQUIRED.** A `select`
               * or `multi` with an empty list violates `metafields_options_match_type` — which
               * until now surfaced as an unhandled Postgres `check_violation` when the wizard
               * saved. There was no input for them at all, so a Jira single-select could not be
               * mapped to a new metafield by any route.
               *
               * ⚠️ **Comma-separated, and split on save rather than on every keystroke.** Splitting
               * as you type eats the comma the moment it is pressed, so the field cannot be typed
               * into — the value shown is the raw text and the array is derived from it.
               */}
              {choice?.kind === 'create' && NEEDS_OPTIONS(choice.type) ? (
                <div className="flex flex-col gap-1 sm:basis-full sm:pl-[172px]">
                  <TextInput
                    value={choice.options.join(', ')}
                    aria-label={t('integrations.fields.newOptions')}
                    placeholder={t('integrations.fields.newOptionsHint')}
                    onChange={(event) => {
                      onChange(field.remoteFieldId, {
                        target: {
                          ...choice,
                          options: event.target.value
                            .split(',')
                            .map((option) => option.trim())
                            .filter((option) => option !== ''),
                        },
                      });
                    }}
                    className="text-[12px]"
                  />
                  {choice.options.length === 0 ? (
                    <span className="text-destructive text-[11px]">
                      {t('integrations.fields.newOptionsRequired')}
                    </span>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
        {mappable.length === 0 ? (
          <p className="text-muted-foreground px-3 py-6 text-center text-[12px]">
            {t('integrations.fields.empty')}
          </p>
        ) : null}
      </div>

      {/* ── the ones we cannot offer, and WHY ────────────────────────────────────────────────── */}
      {rest.length === 0 ? null : (
        <details className="rounded-lg border px-3 py-2">
          <summary className="cursor-pointer text-[12px]">
            {t('integrations.fields.others', { count: rest.length })}
          </summary>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {rest.map((field) => (
              <Badge key={field.remoteFieldId} variant="outline">
                {field.name}
                <span className="text-muted-foreground ml-1">
                  {field.claimed
                    ? t('integrations.fields.claimed')
                    : t('integrations.fields.unsupported')}
                </span>
              </Badge>
            ))}
          </div>
        </details>
      )}

      <p className="text-muted-foreground flex items-start gap-1.5 text-[11.5px] leading-relaxed">
        <Icon name="circle-help" size={13} className="mt-0.5 shrink-0" />
        {t('integrations.fields.note')}
      </p>
    </div>
  );
}
